/**
 * <before-after-slider>
 *
 * Crossfades through pairs of before/after images. Both columns are driven by a
 * single index, so advancing once swaps the BEFORE and the AFTER image together.
 *
 * Expected markup (see snippets/before-after-layer.liquid):
 *
 *   <before-after-slider data-slide-count="3">
 *     <div class="before-after__image-wrap">          <!-- BEFORE column -->
 *       <div data-ba-layer data-ba-index="0" class="is-active">…</div>
 *       <div data-ba-layer data-ba-index="1">…</div>
 *     </div>
 *     <div class="before-after__image-wrap">          <!-- AFTER column -->
 *       <div data-ba-layer data-ba-index="0" class="is-active">…</div>
 *       <div data-ba-layer data-ba-index="1">…</div>
 *     </div>
 *   </before-after-slider>
 *
 * Layers are matched by data-ba-index, not by DOM order, so a pair with only one
 * side filled in still lines up. The fade itself is pure CSS — this only moves
 * the .is-active flag. Nav buttons ([data-ba-prev] / [data-ba-next]) and the
 * counter ([data-ba-current]) live outside the element and are wired up by id
 * via the `nav` attribute. That attribute takes a space-separated list, because
 * the section ships one nav on the title row and another inside the footer (the
 * mobile placement) and CSS shows whichever the breakpoint calls for.
 */
// Wrapped in an IIFE: theme scripts are classic (non-module) scripts sharing one
// global scope, so a top-level `const` here would collide with the same name in
// another asset — newsletter-popup.js already declares `canUseDom`.
(function () {
  const canUseDom =
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    typeof HTMLElement !== "undefined" &&
    typeof customElements !== "undefined";

  if (!canUseDom || customElements.get("before-after-slider")) return;

  class BeforeAfterSlider extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      /** @type {HTMLElement[]} */
      this.navs = [];
      // A swipe only counts once it has travelled this far horizontally and is
      // clearly more horizontal than vertical — otherwise it is a page scroll.
      this.swipeThreshold = 45;
      this.pointer = null;

      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    }

    connectedCallback() {
      /** Every layer of every column, grouped by slide index. */
      this.layers = Array.from(this.querySelectorAll("[data-ba-layer]"));
      this.count = Number(this.dataset.slideCount) || this.countFromLayers();

      // Single pair (or none): stay inert. The Liquid already skips the nav in
      // that case; this is the belt-and-braces half of the same check.
      if (this.count < 2) return;

      // Every nav named by the `nav` attribute — one per placement. Rebuilt from
      // scratch: re-inserting the element runs this again, and a stale copy of
      // the list would keep growing.
      this.navs = [];
      (this.getAttribute("nav") || "")
        .split(/\s+/)
        .filter(Boolean)
        .forEach((id) => {
          const nav = document.getElementById(id);
          if (nav) this.navs.push(nav);
        });

      /** @type {HTMLButtonElement[]} */
      this.prevButtons = this.queryNavs("[data-ba-prev]");
      /** @type {HTMLButtonElement[]} */
      this.nextButtons = this.queryNavs("[data-ba-next]");
      /** @type {HTMLElement[]} */
      this.counters = this.queryNavs("[data-ba-current]");

      this.prevButtons.forEach((button) =>
        button.addEventListener("click", this.handlePrev),
      );
      this.nextButtons.forEach((button) =>
        button.addEventListener("click", this.handleNext),
      );

      this.addEventListener("keydown", this.handleKeydown);
      this.addEventListener("pointerdown", this.handlePointerDown);
      this.addEventListener("transitionend", this.handleTransitionEnd);

      this.setAttribute("tabindex", "0");
      this.setAttribute("role", "group");
      this.setAttribute("aria-roledescription", "carousel");

      // Motionless first paint — no direction, so no slide on load.
      this.render();
    }

    disconnectedCallback() {
      (this.prevButtons || []).forEach((button) =>
        button.removeEventListener("click", this.handlePrev),
      );
      (this.nextButtons || []).forEach((button) =>
        button.removeEventListener("click", this.handleNext),
      );
      this.removeEventListener("keydown", this.handleKeydown);
      this.removeEventListener("pointerdown", this.handlePointerDown);
      this.removeEventListener("transitionend", this.handleTransitionEnd);
      this.releasePointer();
    }

    /**
     * Every match for `selector` across all of the wired-up navs.
     * @param {string} selector
     * @returns {any[]}
     */
    queryNavs(selector) {
      /** @type {any[]} */
      const matches = [];
      this.navs.forEach((nav) => {
        matches.push(...Array.from(nav.querySelectorAll(selector)));
      });
      return matches;
    }

    countFromLayers() {
      return this.layers.reduce(
        (max, layer) => Math.max(max, Number(layer.dataset.baIndex) + 1),
        0,
      );
    }

    /**
     * Wraps around in both directions, so the arrows are never a dead end.
     * `direction` (+1 next / -1 prev) is passed explicitly rather than inferred
     * from the index delta — wrapping from the last pair to the first is a "next"
     * even though the index drops. The crossfade looks the same either way; it is
     * reported on the change event for anything listening in.
     */
    goTo(index, direction) {
      const next = ((index % this.count) + this.count) % this.count;
      if (next === this.index) return;
      const dir = direction || (next > this.index ? 1 : -1);
      this.index = next;
      this.render(dir);
      this.dispatchEvent(
        new CustomEvent("before-after:change", {
          bubbles: true,
          detail: { index: this.index, count: this.count, direction: dir },
        }),
      );
    }

    next() {
      this.goTo(this.index + 1, 1);
    }

    previous() {
      this.goTo(this.index - 1, -1);
    }

    /**
     * @param {number} [direction] +1/-1 to crossfade; omit for the initial,
     *   motionless paint.
     */
    render(direction) {
      const animated = direction === 1 || direction === -1;

      this.layers.forEach((layer) => {
        const isActive = Number(layer.dataset.baIndex) === this.index;

        if (!animated) {
          layer.classList.remove("is-fading", "is-leaving");
          layer.classList.toggle("is-active", isActive);
          layer.toggleAttribute("inert", !isActive);
          layer.setAttribute("aria-hidden", isActive ? "false" : "true");
          return;
        }

        const wasActive = layer.classList.contains("is-active");
        if (isActive && !wasActive) {
          // Incoming: the layer's resting opacity is 0, so adding .is-active and
          // .is-fading together fades it up from wherever it currently sits —
          // including part-way out, if you clicked back before it finished. Both
          // columns are flagged in the same pass, so the BEFORE and AFTER photos
          // fade together as one gesture.
          layer.classList.remove("is-leaving");
          layer.classList.add("is-fading", "is-active");
          layer.removeAttribute("inert");
          layer.setAttribute("aria-hidden", "false");
        } else if (!isActive && wasActive) {
          // Outgoing: .is-leaving holds it opaque underneath the incoming layer
          // and CSS cuts it once the fade lands — see the delayed transition
          // there. The class is stripped below on transitionend.
          layer.classList.remove("is-active", "is-fading");
          layer.classList.add("is-leaving");
          layer.setAttribute("inert", "");
          layer.setAttribute("aria-hidden", "true");
        } else if (!layer.classList.contains("is-leaving")) {
          // Neither side of this change. A layer still leaving from an earlier,
          // rapid click is left alone so it can finish its own cut rather than
          // vanishing out from under the fade.
          layer.classList.remove("is-active", "is-fading");
          layer.setAttribute("inert", "");
          layer.setAttribute("aria-hidden", "true");
        }
      });

      this.updateCounter();
    }

    handleTransitionEnd(event) {
      if (event.propertyName !== "opacity") return;
      const layer = event.target.closest("[data-ba-layer]");
      if (layer && layer.classList.contains("is-leaving")) {
        layer.classList.remove("is-leaving");
      }
    }

    updateCounter() {
      const label = String(this.index + 1).padStart(2, "0");
      (this.counters || []).forEach((counter) => {
        if (counter.textContent === label) return;
        counter.textContent = label;
        // Restart the CSS animation: drop the class, force a reflow so the
        // browser sees it gone, then re-add it. Without the reflow, removing and
        // re-adding in one frame is a no-op and the digit swaps flatly.
        counter.classList.remove("is-updating");
        void counter.offsetWidth;
        counter.classList.add("is-updating");
      });
    }

    handlePrev(event) {
      event.preventDefault();
      this.previous();
    }

    handleNext(event) {
      event.preventDefault();
      this.next();
    }

    handleKeydown(event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.previous();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.next();
      }
    }

    handlePointerDown(event) {
      // Let the footer button, links and the nav keep their native behaviour.
      if (event.target.closest("a, button")) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.pointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      window.addEventListener("pointermove", this.handlePointerMove, {
        passive: true,
      });
      window.addEventListener("pointerup", this.handlePointerEnd);
      window.addEventListener("pointercancel", this.handlePointerEnd);
    }

    handlePointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;

      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;

      // Vertical intent wins: hand the gesture back to the page scroll.
      if (Math.abs(dy) > Math.abs(dx)) {
        this.releasePointer();
        return;
      }

      if (Math.abs(dx) < this.swipeThreshold) return;

      this.releasePointer();
      if (dx < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    handlePointerEnd() {
      this.releasePointer();
    }

    releasePointer() {
      if (!this.pointer) return;
      this.pointer = null;
      window.removeEventListener("pointermove", this.handlePointerMove);
      window.removeEventListener("pointerup", this.handlePointerEnd);
      window.removeEventListener("pointercancel", this.handlePointerEnd);
    }
  }

  customElements.define("before-after-slider", BeforeAfterSlider);
})();
